import React, { useEffect, useState } from "react";
import portalApi from "../../api/portalClient";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { useBranding } from "../../context/BrandingContext";
import { PortalInvoiceSummary } from "../../types";
import { useSort, sortRows, SortableTh } from "../../components/SortableTh";

function fmtCents(c: number): string {
  return `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtMins(m: number): string {
  return `${(m / 60).toFixed(1)} hr`;
}

export default function PortalInvoices() {
  const { user } = usePortalAuth();
  const branding = useBranding();
  const [invoices, setInvoices] = useState<PortalInvoiceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const canViewInvoices = user?.can_view_invoices !== false;
  const invSort = useSort("period_start", "desc");

  useEffect(() => {
    if (!canViewInvoices) {
      setLoading(false);
      return;
    }
    portalApi
      .get("/api/v1/portal/invoices")
      .then((r) => setInvoices(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [canViewInvoices]);

  if (!canViewInvoices) {
    return (
      <div className="max-w-6xl">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">Invoices</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Billing statements and history.</p>
        <div className="bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-700 rounded-xl p-5 text-sm text-amber-900 dark:text-amber-200">
          <p className="font-semibold mb-1">Invoice access isn't enabled for your account.</p>
          <p>
            Contact your administrator at {branding.company_name} if you need to view billing
            statements — they can enable this from the customer portal admin panel. Otherwise
            reach{" "}
            <a
              href={`mailto:${branding.portal_support_email}`}
              className="text-brand dark:text-brand-light font-medium hover:underline"
            >
              {branding.portal_support_email}
            </a>{" "}
            for help.
          </p>
        </div>
      </div>
    );
  }

  const ytd = invoices.reduce((s, i) => s + i.billable_amount_cents, 0);
  const ytdHours = invoices.reduce((s, i) => s + i.billable_minutes, 0);

  return (
    <div className="max-w-6xl">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">Invoices</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Monthly statements emailed from {branding.company_name}. Reach out to{" "}
        <a
          href={`mailto:${branding.portal_support_email}`}
          className="text-brand dark:text-brand-light hover:underline font-medium"
        >
          support
        </a>{" "}
        with billing questions.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <SummaryCard label="Last 12 months" value={fmtCents(ytd)} accent="bg-brand" />
        <SummaryCard label="Billable hours" value={fmtMins(ytdHours)} accent="bg-blue-500" />
        <SummaryCard
          label="Months on record"
          value={String(invoices.length)}
          accent="bg-gray-400"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Billing history</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <SortableTh label="Period" sortKey="period_start" sort={invSort.sort} onSort={invSort.toggle} />
                <SortableTh label="Billable" sortKey="billable_minutes" sort={invSort.sort} onSort={invSort.toggle} align="right" />
                <SortableTh label="Non-Billable" sortKey="non_billable_minutes" sort={invSort.sort} onSort={invSort.toggle} align="right" />
                <SortableTh label="Entries" sortKey="entry_count" sort={invSort.sort} onSort={invSort.toggle} align="right" />
                <SortableTh label="Amount" sortKey="billable_amount_cents" sort={invSort.sort} onSort={invSort.toggle} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                    No statements yet.
                  </td>
                </tr>
              )}
              {sortRows(invoices, invSort.sort, (i, key) => {
                switch (key) {
                  case "billable_minutes":
                    return i.billable_minutes;
                  case "non_billable_minutes":
                    return i.non_billable_minutes;
                  case "entry_count":
                    return i.entry_count;
                  case "billable_amount_cents":
                    return i.billable_amount_cents;
                  default:
                    return i.period_start ? Date.parse(i.period_start) : 0;
                }
              }).map((i) => (
                <tr key={i.period_label} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/40 transition-colors">
                  <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">{i.period_label}</td>
                  <td className="px-5 py-3 text-right text-gray-700 dark:text-gray-300">{fmtMins(i.billable_minutes)}</td>
                  <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">
                    {fmtMins(i.non_billable_minutes)}
                  </td>
                  <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">{i.entry_count}</td>
                  <td className="px-5 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                    {fmtCents(i.billable_amount_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150">
      <div className={`absolute top-0 left-0 w-1 h-full ${accent}`} />
      <div className="pl-5 pr-4 py-4">
        <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}
